import { corsUrl, loadBitmap, type AssetCatalog } from './assets';
import type { ShowLayer } from './character-sheet';

/** Parte do modelo de item que decide o ícone (`categoryId` e `pic` do catálogo do legado). */
export interface IconTemplate {
  categoryId: number;
  pic: string;
}

/** Onde procurar o ícone: o atlas (JSON) e os nomes de quadro aceitos, em ordem de preferência. */
export interface IconSource {
  atlas: string;
  /** Nomes exatos de quadro; o primeiro que existir vale. Vazio: o primeiro quadro que termina em `icon` ou `show`. */
  frames: string[];
}

/** Pasta de cada peça de visual por categoria do legado (`Shop_Goods.CategoryID`). */
const LOOK_SLOT: Record<number, string> = { 1: 'head', 2: 'glass', 3: 'hair', 4: 'eff', 5: 'cloth', 6: 'face' };
/** Atlas compartilhado, com um quadro `<pic>/icon` por item. */
const SHARED_ATLAS: Record<number, string> = { 8: 'armlet', 9: 'ring', 14: 'necklace', 15: 'wing', 17: 'offhand', 19: 'recover', 28: 'armlet', 29: 'ring' };
/**
 * Categorias com um atlas próprio, de um quadro `<pic>/icon` por item. No legado, `PathManager.solveGoodsPath` lê `image/<pasta>/<pic>/icon.png`:
 * 11 é `UNFRIGHTPROP` (pedras de fortalecer, composição e encaixe, amuletos, materiais), 12 é `TASK`, 18 é `CARDBOX`, 25 é `GIFTGOODS` e 26 é `CARDEQUIP`.
 */
const ICON_FOLDER: Record<number, string> = { 11: 'unfrightprop', 12: 'task', 18: 'cardbox', 25: 'gift', 26: 'card' };
/** Atlas da interface da mochila: os consumíveis (categoria 10) usam os quadros `Prop<pic>Asset`, os mesmos ícones das armas de partida. */
const PROP_ATLAS = 'atlas/swf/Arquivos_para_IIS/ui/portugal/swf/bagAndInfo/Arquivos_para_IIS_ui_portugal_swf_bagAndInfo.json';
/** Categorias de arma (27 são as variações de arma do legado, com a arte da arma de mesmo nome). */
const WEAPON_CATEGORIES = new Set([7, 27]);

/**
 * Menor retângulo que contém os pixels visíveis (opacidade acima de `minAlpha`) de uma imagem RGBA, ou `null` se estiver vazia.
 * Serve para centralizar o ícone: peças de visual vêm numa tela de 250 × 312 px, onde o item fica na posição que ocupa no corpo.
 */
export function opaqueBounds(data: ArrayLike<number>, w: number, h: number, minAlpha = 24): { x: number; y: number; w: number; h: number } | null {
  let x0 = w;
  let y0 = h;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3]! > minAlpha) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }

  return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/** Tela do boneco em pé, onde os quadros `show` das peças de visual são desenhados. */
const LOOK_CANVAS = { w: 250, h: 312 };

/**
 * Atlas onde o ícone de um item deve estar, do mais provável ao menos. A arte antiga não tem um ícone de loja por item: ele sai
 * dos quadros `icon` (armas, bijuterias) ou `show` (peças de visual) dos atlas de equipamento. Item sem arte devolve lista vazia.
 */
export function iconSources(template: IconTemplate): IconSource[] {
  const { categoryId: category, pic } = template;
  if (!pic) return [];
  const root = 'atlas/images';
  if (WEAPON_CATEGORIES.has(category)) return [{ atlas: `${root}/arm/${pic}/arm_${pic}.json`, frames: [] }];
  if (category === 10 && /^\d+$/.test(pic)) return [{ atlas: PROP_ATLAS, frames: [`*game.crazyTank.view.Prop${pic}Asset`] }];
  if (category === 16) return [{ atlas: `${root}/specialprop/chatBall/specialprop_chatBall.json`, frames: [`${pic}/icon`] }];
  const folder = ICON_FOLDER[category];
  if (folder) return [{ atlas: `${root}/${folder}/${folder}.json`, frames: [`${pic}/icon`] }];
  const shared = SHARED_ATLAS[category];
  if (shared) return [{ atlas: `${root}/equip/${shared}/equip_${shared}.json`, frames: [`${pic}/icon`] }];
  if (category === 13) {
    return [
      { atlas: `${root}/equip/suits/equip_suits.json`, frames: [`${pic}/icon_1`] },
      { atlas: `${root}/equip/suits/${pic}/equip_suits_${pic}.json`, frames: [] },
    ];
  }
  const slot = LOOK_SLOT[category];
  if (!slot) return [];
  const sources: IconSource[] = [];
  for (const sex of ['m', 'f']) {
    sources.push({ atlas: `${root}/equip/${sex}/${slot}/${pic}/equip_${sex}_${slot}_${pic}.json`, frames: [] });
    // Cabelo tem uma pasta por variação de cor, com as camadas A e B.
    if (slot === 'hair') sources.push({ atlas: `${root}/equip/${sex}/hair/${pic}/1/equip_${sex}_hair_${pic}_1.json`, frames: ['A/show', 'B/show'] });
  }
  return sources;
}

export interface AtlasFrame {
  frame: { x: number; y: number; w: number; h: number };
  rotated?: boolean;
  spriteSourceSize?: { x: number; y: number; w: number; h: number };
  sourceSize?: { w: number; h: number };
}

export interface AtlasFile {
  frames: Record<string, AtlasFrame>;
}

/** Escolhe o quadro do ícone no atlas; `null` se não houver nenhum utilizável (quadro girado não é suportado). */
export function pickFrame(atlas: AtlasFile, wanted: readonly string[]): { name: string; frame: AtlasFrame } | null {
  const names = Object.keys(atlas.frames);
  // Nome com `*` na frente casa pelo final: os quadros da interface vêm com um número de ordem antes do nome (`19_game...Prop19Asset`).
  const resolved = wanted.flatMap((w) => (w.startsWith('*') ? names.filter((n) => n.endsWith(`_${w.slice(1)}`)).slice(0, 1) : w in atlas.frames ? [w] : []));
  const candidates = wanted.length > 0 ? resolved : [...names.filter((n) => /(^|\/)icon$/.test(n)), ...names.filter((n) => /(^|\/)show$/.test(n))];
  for (const name of candidates) {
    const frame = atlas.frames[name];
    if (frame && !frame.rotated && frame.frame.w > 0 && frame.frame.h > 0) return { name, frame };
  }
  return null;
}

/**
 * Ícones de item montados a partir dos atlas do pacote de arte: baixa o atlas e a folha uma vez, recorta o quadro e devolve uma URL de
 * imagem (PNG). Cada item é resolvido uma vez; item sem arte devolve `null` e a tela mostra um quadrado no lugar.
 */
export class ItemIcons {
  private readonly atlases = new Map<string, Promise<{ atlas: AtlasFile; sheet: ImageBitmap } | null>>();
  private readonly icons = new Map<string, Promise<string | null>>();
  private readonly composites = new Map<string, Promise<string | null>>();

  constructor(
    private readonly assets: AssetCatalog,
    private readonly fetchImpl: typeof fetch = (...args) => fetch(...args),
  ) {}

  /** URL do ícone do item (`blob:`), ou `null` se a arte antiga não tem imagem para ele. */
  url(template: IconTemplate): Promise<string | null> {
    const key = `${template.categoryId}:${template.pic}`;
    let icon = this.icons.get(key);
    if (!icon) {
      icon = this.resolve(template).catch(() => null);
      this.icons.set(key, icon);
    }
    return icon;
  }

  /**
   * Empilha quadros de vários atlas (de trás para a frente) numa tela de `size`, cada um na posição que a arte original lhe deu, e
   * devolve como imagem (`blob:`). O que passa da tela é cortado (a folha de rosto traz outras expressões ao lado). Quadro que não
   * existe é pulado. `null` se nada pôde ser desenhado.
   */
  composite(layers: readonly ShowLayer[], size: { w: number; h: number }): Promise<string | null> {
    const key = `${size.w}x${size.h}|${layers.map((l) => `${l.atlas}#${l.frame}`).join(',')}`;
    let image = this.composites.get(key);
    if (!image) {
      image = this.drawLayers(layers, size).catch(() => null);
      this.composites.set(key, image);
    }
    return image;
  }

  /**
   * O quadro de um atlas sozinho, do tamanho original (sem a margem transparente), como imagem (`blob:`). `null` se o quadro não existe.
   * Serve às asas, que passam da tela do boneco e por isso ficam numa imagem própria.
   */
  frameImage(atlas: string, frame: string): Promise<{ url: string; width: number; height: number } | null> {
    const key = `${atlas}#${frame}`;
    let image = this.frames.get(key);
    if (!image) {
      image = this.drawFrame(atlas, frame).catch(() => null);
      this.frames.set(key, image);
    }
    return image;
  }

  private readonly frames = new Map<string, Promise<{ url: string; width: number; height: number } | null>>();

  private async drawFrame(atlas: string, frame: string): Promise<{ url: string; width: number; height: number } | null> {
    const loaded = this.assets.has(atlas) ? await this.atlas(atlas) : null;
    const f = loaded?.atlas.frames[frame];
    if (!loaded || !f || f.rotated) return null;
    const canvas = document.createElement('canvas');
    canvas.width = f.frame.w;
    canvas.height = f.frame.h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(loaded.sheet, f.frame.x, f.frame.y, f.frame.w, f.frame.h, 0, 0, f.frame.w, f.frame.h);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    return blob ? { url: URL.createObjectURL(blob), width: f.frame.w, height: f.frame.h } : null;
  }

  /** Como `composite`, mas devolve a tela desenhada (sem cache), para quem precisa ler os pixels. */
  compositeCanvas(layers: readonly ShowLayer[], size: { w: number; h: number }): Promise<HTMLCanvasElement | null> {
    return this.paint(layers, size).catch(() => null);
  }

  private async drawLayers(layers: readonly ShowLayer[], size: { w: number; h: number }): Promise<string | null> {
    const canvas = await this.paint(layers, size);
    if (!canvas) return null;
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    return blob ? URL.createObjectURL(blob) : null;
  }

  private async paint(layers: readonly ShowLayer[], size: { w: number; h: number }): Promise<HTMLCanvasElement | null> {
    const canvas = document.createElement('canvas');
    canvas.width = size.w;
    canvas.height = size.h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    // Como o jogo original: cada camada é desenhada inteira, de trás para a frente. A versão do cabelo (A ou B) já vem escolhida em `showLayers`.
    let drawn = 0;
    for (const layer of layers) {
      const placed = await this.placement(layer, size);
      if (!placed) continue;
      ctx.drawImage(placed.sheet, placed.sx, placed.sy, placed.w, placed.h, placed.x, placed.y, placed.w, placed.h);
      drawn++;
    }

    return drawn === 0 ? null : canvas;
  }

  /** Onde o quadro da camada cai na tela do boneco (recorte da folha e destino), já cortado pela tela. `null` se não há o que desenhar. */
  private async placement(
    layer: ShowLayer,
    size: { w: number; h: number },
  ): Promise<{ sheet: ImageBitmap; sx: number; sy: number; x: number; y: number; w: number; h: number } | null> {
    const loaded = this.assets.has(layer.atlas) ? await this.atlas(layer.atlas) : null;
    const f = loaded?.atlas.frames[layer.frame];
    if (!loaded || !f || f.rotated) return null;
    const offset = f.spriteSourceSize ?? { x: 0, y: 0 };
    const w = Math.min(f.frame.w, size.w - offset.x);
    const h = Math.min(f.frame.h, size.h - offset.y);
    if (w <= 0 || h <= 0) return null;
    return { sheet: loaded.sheet, sx: f.frame.x, sy: f.frame.y, x: offset.x, y: offset.y, w, h };
  }

  private async resolve(template: IconTemplate): Promise<string | null> {
    for (const source of iconSources(template)) {
      if (!this.assets.has(source.atlas)) continue;
      const loaded = await this.atlas(source.atlas);
      const picked = loaded ? pickFrame(loaded.atlas, source.frames) : null;
      if (!loaded || !picked) continue;
      // Peça de visual (quadro `show`): o item está na posição do corpo, numa tela grande. Corta no contorno para centralizar.
      if (/(^|\/)show$/.test(picked.name)) {
        const names = ['B/show', 'A/show'].every((n) => n in loaded.atlas.frames) ? ['B/show', 'A/show'] : [picked.name];
        const look = await this.renderLook(loaded, names);
        if (look) return look;
      }

      return this.render(loaded.sheet, picked.frame);
    }
    return null;
  }

  private atlas(path: string): Promise<{ atlas: AtlasFile; sheet: ImageBitmap } | null> {
    let loaded = this.atlases.get(path);
    if (!loaded) {
      loaded = (async () => {
        const sheetPath = path.replace(/\.json$/, '.webp');
        if (!this.assets.has(sheetPath)) return null;
        const response = await this.fetchImpl(corsUrl(this.assets.url(path)));
        if (!response.ok) return null;
        const atlas = (await response.json()) as AtlasFile;
        const sheet = await loadBitmap(this.assets.url(sheetPath), this.fetchImpl);
        return sheet ? { atlas, sheet } : null;
      })().catch(() => null);
      this.atlases.set(path, loaded);
    }
    return loaded;
  }

  /** Desenha os quadros (de trás para a frente) na tela do boneco, corta no contorno visível e devolve como imagem. */
  private async renderLook(loaded: { atlas: AtlasFile; sheet: ImageBitmap }, names: readonly string[]): Promise<string | null> {
    const { w, h } = LOOK_CANVAS;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    for (const name of names) {
      const f = loaded.atlas.frames[name];
      if (!f || f.rotated) continue;
      const offset = f.spriteSourceSize ?? { x: 0, y: 0 };
      const dw = Math.min(f.frame.w, w - offset.x);
      const dh = Math.min(f.frame.h, h - offset.y);
      if (dw > 0 && dh > 0) ctx.drawImage(loaded.sheet, f.frame.x, f.frame.y, dw, dh, offset.x, offset.y, dw, dh);
    }

    const box = opaqueBounds(ctx.getImageData(0, 0, w, h).data, w, h);
    if (!box) return null;
    const pad = 2;
    const x = Math.max(0, box.x - pad);
    const y = Math.max(0, box.y - pad);
    const cut = document.createElement('canvas');
    cut.width = Math.min(w, box.x + box.w + pad) - x;
    cut.height = Math.min(h, box.y + box.h + pad) - y;
    cut.getContext('2d')?.drawImage(canvas, x, y, cut.width, cut.height, 0, 0, cut.width, cut.height);
    const blob = await new Promise<Blob | null>((resolve) => cut.toBlob(resolve, 'image/png'));
    return blob ? URL.createObjectURL(blob) : null;
  }

  /** Recorta o quadro numa tela do tamanho original (com a margem que o empacotador tirou) e devolve como imagem. */
  private async render(sheet: ImageBitmap, f: AtlasFrame): Promise<string | null> {
    const size = f.sourceSize ?? { w: f.frame.w, h: f.frame.h };
    const offset = f.spriteSourceSize ?? { x: 0, y: 0, w: f.frame.w, h: f.frame.h };
    const canvas = document.createElement('canvas');
    canvas.width = size.w;
    canvas.height = size.h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(sheet, f.frame.x, f.frame.y, f.frame.w, f.frame.h, offset.x, offset.y, f.frame.w, f.frame.h);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    return blob ? URL.createObjectURL(blob) : null;
  }
}
